/**
 * Config contact / partage.
 *
 * Le numéro WhatsApp reçoit toutes les commandes. Il se règle en
 * administration et arrive par `GET /api/contact` : le figer dans le bundle
 * imposait un rebuild et un redéploiement pour en changer, autant dire une
 * soirée sans ligne de commande le jour où la puce change de main.
 *
 * Trois niveaux, du plus frais au plus sûr : la valeur reçue de l'API, celle
 * retenue du dernier chargement, puis celle-ci. Le repli compte : le bouton
 * WhatsApp doit fonctionner même si l'appel échoue.
 */
const WHATSAPP_STORAGE_KEY = "chinexpress_whatsapp_number";

export const WHATSAPP_NUMBER_FALLBACK =
  process.env.REACT_APP_WHATSAPP_NUMBER || "24174459592";

/** Valeur reçue de l'API pour cette session. */
let runtimeWhatsAppNumber = null;

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

/**
 * Retient le numéro servi par l'API, pour cette session et pour le prochain
 * démarrage — sans quoi le premier rendu qui suit un rechargement repartirait
 * sur le repli, le temps que l'appel revienne.
 */
export const setWhatsAppNumber = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return;

  runtimeWhatsAppNumber = digits;
  try {
    localStorage.setItem(WHATSAPP_STORAGE_KEY, digits);
  } catch {
    /* navigation privée : la valeur de session suffit */
  }
};

/** Numéro à composer maintenant. */
export const getWhatsAppNumber = () => {
  if (runtimeWhatsAppNumber) return runtimeWhatsAppNumber;

  try {
    const stored = onlyDigits(localStorage.getItem(WHATSAPP_STORAGE_KEY));
    if (stored) return stored;
  } catch {
    /* ignore */
  }

  return onlyDigits(WHATSAPP_NUMBER_FALLBACK);
};

const fmtFcfa = (n) =>
  `${Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} FCFA`;

/** Preview Open Graph du lien (carte WhatsApp) — unique pour toute l'app */
export const APP_SHARE = {
  title: "Chinexpress - Pense business",
  description:
    "Achète au prix du marché chinois, revends et gagne. Rejoins la communauté des gens qui pensent business.",
  url:
    typeof window !== "undefined"
      ? `${window.location.origin}/`
      : "https://chinexpress.com/",
};

const CTA_OPPORTUNITY = "Découvre l'opportunité 👇🏽";
const CTA_JOIN = "Rejoins-nous 👇🏽";

/**
 * Textes de partage par contexte.
 * - whatsappBody → message prérempli WhatsApp (sans titre ; + lien à l'envoi)
 * - description → texte affiché dans la pop-up
 */
export const SHARE_COPY = {
  app: {
    modalTitle: "Partage Chinexpress",
    description:
      "Invite tes proches à rejoindre Chinexpress et à faire partie de la communauté des gens qui pensent business.",
    title: null,
    whatsappBody: () =>
      `🚀 Rejoins la communauté des businessmen sur Chinexpress ! Découvre de nouvelles opportunités, développe ton business et faisons du business ensemble. 💼\n\n${CTA_JOIN}`,
  },
  business: {
    modalTitle: "Partage et gagne",
    title: null,
    /** @param {{ budget:number, pieces:number, profit:number }} offer */
    description: ({ budget, pieces, profit }) =>
      `💰 Avec ${fmtFcfa(budget)}, achète ${Number(pieces) || 0} pièces de ce produit et réalise un bénéfice estimé de ${fmtFcfa(profit)}.`,
    /** @param {{ pieces:number, profit:number, unitPrice:number }} offer */
    whatsappBody: ({ pieces, profit, unitPrice }) =>
      `💰 Gagne un bénéfice estimé de ${fmtFcfa(profit)} en achetant seulement ${Number(pieces) || 0} pièces de ce produit à ${fmtFcfa(unitPrice)} l'unité.\n\n${CTA_OPPORTUNITY}`,
  },
  standard: {
    modalTitle: "Partage et gagne",
    title: null,
    /** @param {{ price:number }} ctx */
    description: ({ price }) =>
      `📦 Achète malin, importe ce produit depuis la Chine à seulement ${fmtFcfa(price)} et fais des économies. Pense business.`,
    whatsappBody: ({ price }) =>
      `📦 Achète malin, importe ce produit depuis la Chine à seulement ${fmtFcfa(price)} et fais des économies. Pense business.\n\n${CTA_OPPORTUNITY}`,
  },
  sample: {
    modalTitle: "Partage et gagne",
    title: null,
    /** @param {{ price:number }} ctx */
    description: ({ price }) =>
      `🔥 Profite de ce produit à seulement ${fmtFcfa(price)} avant rupture de stock.`,
    whatsappBody: ({ price }) =>
      `🔥 Profite de ce produit à seulement ${fmtFcfa(price)} avant rupture de stock.\n\n${CTA_OPPORTUNITY}`,
  },
};

/**
 * @param {'app'|'business'|'standard'|'sample'} kind
 * @param {{ price?: number, budget?: number, pieces?: number, profit?: number, unitPrice?: number }} [ctx]
 */
export const resolveShareCopy = (kind, ctx = {}) => {
  const params = {
    price: ctx.price ?? 0,
    budget: ctx.budget ?? 0,
    pieces: ctx.pieces ?? 0,
    profit: ctx.profit ?? 0,
    unitPrice: ctx.unitPrice ?? 0,
  };

  const copy = SHARE_COPY[kind] || SHARE_COPY.app;
  const description =
    typeof copy.description === "function"
      ? copy.description(params)
      : copy.description;
  // Message WhatsApp = corps uniquement (jamais le titre), CTA après une ligne vide
  const whatsappPrefill =
    typeof copy.whatsappBody === "function"
      ? copy.whatsappBody(params)
      : copy.whatsappBody;

  return {
    modalTitle: copy.modalTitle,
    title: copy.title || APP_SHARE.title,
    description,
    whatsappPrefill,
  };
};

/**
 * Message WhatsApp prérempli.
 * Format : paragraphe, ligne vide, CTA, ligne vide, lien.
 * Ne jamais inclure de titre (« Chinexpress - … ») dans le corps.
 */
export const buildWhatsAppShareText = (prefill, pageUrl) => {
  const body = String(prefill || "")
    .replace(/^Chinexpress\s*-\s*[^\n]+\n+/i, "")
    .trim();
  return `${body}\n\n${pageUrl}`;
};
/** Déduit le type de partage produit (hors app / business explicite). */
export const detectProductShareKind = (product) => {
  if (!product) return "standard";
  if (
    product.isEchantillon ||
    product.product_type === "sample" ||
    product.type === "sample" ||
    product.sampleAvailable === true
  ) {
    return "sample";
  }
  return "standard";
};

export const buildWhatsAppUrl = (message = "") => {
  const digits = getWhatsAppNumber();
  if (!message || !String(message).trim()) {
    // Discussion support : sans message (évite de réinjecter un ancien brouillon ?text=…)
    return `https://wa.me/${digits}`;
  }
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

/** Ouvre WhatsApp support sans aucun texte prérempli */
export const buildWhatsAppSupportUrl = () => buildWhatsAppUrl("");
